import type { Permission, PermissionCode } from '../../types';

/**
 * Nhãn tiếng Việt cho từng permission code — dùng khi API /permissions không
 * trả `description`, hoặc khi cần fallback (API 404).
 */
export const PERMISSION_LABELS: Record<PermissionCode, string> = {
  PROJECT_VIEW: 'Xem dự án',
  PHASE_VIEW: 'Xem giai đoạn',
  PHASE_CREATE: 'Tạo giai đoạn',
  PHASE_UPDATE: 'Sửa giai đoạn',
  PHASE_DELETE: 'Xóa giai đoạn',
  FEATURE_VIEW: 'Xem tính năng',
  FEATURE_CREATE: 'Tạo tính năng',
  FEATURE_UPDATE: 'Sửa tính năng',
  FEATURE_DELETE: 'Xóa tính năng',
  FEATURE_CONTRACT_TAG_UPDATE: 'Cập nhật nhãn hợp đồng của tính năng',
  FEATURE_IMAGE_VIEW: 'Xem hình ảnh tính năng',
  FEATURE_IMAGE_CREATE: 'Tải lên hình ảnh tính năng',
  FEATURE_IMAGE_DELETE: 'Xóa hình ảnh tính năng',
  ACCEPTANCE_VIEW: 'Xem nghiệm thu',
  FEATURE_CONFIRM: 'Xác nhận tính năng',
  FEATURE_CONFIRM_BULK: 'Đánh dấu hoàn thành hàng loạt (Admin)',
  FEATURE_CONFIRM_REVOKE: 'Thu hồi nghiệm thu (Admin)',
  CHANGE_REQUEST_VIEW: 'Xem yêu cầu thay đổi (khách hàng)',
  CHANGE_REQUEST_CREATE: 'Tạo yêu cầu thay đổi (khách hàng)',
  CHANGED_REQUEST_VIEW: 'Xem yêu cầu thay đổi (xử lý)',
  CHANGED_REQUEST_UPDATE: 'Cập nhật yêu cầu thay đổi (xử lý)',
  CHANGED_REQUEST_RESOLVE: 'Giải quyết yêu cầu thay đổi',
  BUG_VIEW: 'Xem danh sách lỗi (bug)',
  BUG_CREATE: 'Thêm lỗi (bug)',
  BUG_UPDATE: 'Cập nhật lỗi / đổi trạng thái',
  BUG_DELETE: 'Xóa lỗi (bug)',
  BUG_COMMENT: 'Bình luận trên lỗi (bug)',
  BUG_VERIFY: 'Xác minh lỗi đã được sửa',
  BUG_HIDE: 'Ẩn lỗi (bug) khỏi danh sách sau khi đã đóng',
  BUG_SEVERITY_EDIT: 'Chỉnh mức độ nghiêm trọng của lỗi (bug)',
  FEATURE_REQUEST_VIEW: 'Xem yêu cầu chức năng',
  FEATURE_REQUEST_CREATE: 'Tạo yêu cầu chức năng',
  FEATURE_REQUEST_VIEW_ALL: 'Xem tất cả yêu cầu chức năng',
  FEATURE_REQUEST_CONVERT: 'Chuyển yêu cầu chức năng thành tính năng',
  FEATURE_REQUEST_DELETE: 'Xóa yêu cầu chức năng',
  FEATURE_REQUEST_COMMENT: 'Bình luận trên yêu cầu chức năng',
  FEATURE_REQUEST_CONFIRM: 'Xác nhận yêu cầu chức năng (khách hàng)',
  PROJECT_DOCUMENT_VIEW: 'Xem tài liệu dự án',
  PROJECT_DOCUMENT_CREATE: 'Tạo/tải lên tài liệu dự án',
  PROJECT_DOCUMENT_DELETE: 'Xóa tài liệu dự án',
};

/** Nhóm các permission code theo nghiệp vụ — dùng cho checklist RolePermissionDialog. */
export const PERMISSION_GROUPS: { title: string; codes: PermissionCode[] }[] = [
  { title: 'Dự án', codes: ['PROJECT_VIEW'] },
  { title: 'Giai đoạn', codes: ['PHASE_VIEW', 'PHASE_CREATE', 'PHASE_UPDATE', 'PHASE_DELETE'] },
  {
    title: 'Tính năng',
    codes: [
      'FEATURE_VIEW',
      'FEATURE_CREATE',
      'FEATURE_UPDATE',
      'FEATURE_DELETE',
      'FEATURE_CONTRACT_TAG_UPDATE',
      'FEATURE_CONFIRM_BULK',
    ],
  },
  {
    title: 'Hình ảnh tính năng',
    codes: ['FEATURE_IMAGE_VIEW', 'FEATURE_IMAGE_CREATE', 'FEATURE_IMAGE_DELETE'],
  },
  { title: 'Nghiệm thu', codes: ['ACCEPTANCE_VIEW', 'FEATURE_CONFIRM', 'FEATURE_CONFIRM_REVOKE'] },
  {
    title: 'Yêu cầu thay đổi (khách hàng)',
    codes: ['CHANGE_REQUEST_VIEW', 'CHANGE_REQUEST_CREATE'],
  },
  {
    title: 'Yêu cầu thay đổi (xử lý)',
    codes: ['CHANGED_REQUEST_VIEW', 'CHANGED_REQUEST_UPDATE', 'CHANGED_REQUEST_RESOLVE'],
  },
  {
    title: 'Lỗi (bug)',
    codes: [
      'BUG_VIEW',
      'BUG_CREATE',
      'BUG_UPDATE',
      'BUG_DELETE',
      'BUG_COMMENT',
      'BUG_VERIFY',
      'BUG_HIDE',
      'BUG_SEVERITY_EDIT',
    ],
  },
  {
    title: 'Yêu cầu chức năng',
    codes: ['FEATURE_REQUEST_VIEW', 'FEATURE_REQUEST_CREATE', 'FEATURE_REQUEST_VIEW_ALL', 'FEATURE_REQUEST_CONVERT', 'FEATURE_REQUEST_DELETE', 'FEATURE_REQUEST_COMMENT', 'FEATURE_REQUEST_CONFIRM'],
  },
  {
    title: 'Tài liệu dự án',
    codes: ['PROJECT_DOCUMENT_VIEW', 'PROJECT_DOCUMENT_CREATE', 'PROJECT_DOCUMENT_DELETE'],
  },
];

const NOW_ISO = new Date(0).toISOString();

/** Danh sách quyền dự phòng khi GET /api/permissions chưa sẵn sàng (404). */
export const FALLBACK_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) => g.codes).map(
  (code) => ({
    id: code,
    code,
    description: PERMISSION_LABELS[code],
    created_at: NOW_ISO,
    updated_at: NOW_ISO,
    deleted_at: null,
  }),
);
